<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>{{ $t('walls.form.name.label') }}</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            :placeholder="$t('walls.form.name.placeholder')"
          />
          <d-form-invalid-feedback>
            {{ $t('walls.form.name.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
        <d-col
          cols="12"
          md="4"
          class="d-flex align-items-end justify-content-around"
        >
          <d-checkbox
            toggle
            v-model="model.settings.showAnswer"
            class="custom-toggle-sm"
          >
            <span>{{ $t('walls.form.showAnswer.label') }}</span>
          </d-checkbox>
          <d-checkbox
            toggle
            v-model="model.settings.colored"
            class="custom-toggle-sm"
          >
            <span>{{ $t('walls.form.colored.label') }}</span>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-end mt-3">
        <d-col cols="12" md="2">
          <label>{{ $t('walls.form.columns.label') }}</label>
          <d-form-input
            type="number"
            :min="minDimensions.COLUMNS"
            v-model.number="model.columns"
            @input="updateTable"
          />
        </d-col>
        <d-col cols="12" md="2">
          <label>{{ $t('walls.form.rows.label') }}</label>
          <d-form-input
            type="number"
            :min="minDimensions.ROWS"
            v-model.number="model.rows"
            @input="updateTable"
          />
        </d-col>
        <d-col cols="12" md="2">
          <label>
            {{ $t('walls.form.level.label') }}
            <i
              id="tooltip-density-style"
              class="fas fa-info-circle text-muted ml-1"
              style="cursor: pointer"
            />
            <d-tooltip target="tooltip-density-style" placement="top">
              {{ $t('walls.form.level.tooltip') }}
            </d-tooltip>
          </label>
          <custom-select
            v-model="levelOption"
            :options="levelOptions"
            trackBy="slug"
            :placeholder="$t('walls.form.level.placeholder')"
            @change="setLevel"
          >
            <template #option="{ option }">
              <i :class="option.color" class="fas fa-circle mr-2" />
              {{ $t(option.labelKey) }}
            </template>
            <template #selected="{ option }">
              {{ $t(option.labelKey) }}
            </template>
          </custom-select>
          <d-form-invalid-feedback>
            {{ $t('walls.form.level.invalid') }}
          </d-form-invalid-feedback>
        </d-col>
      </d-form-row>
    </d-form>

    <div class="position-relative">
      <board-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
        :settings="model.settings"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import mixin from '_common/mixins/ValidateForm';
import { cloneDeep, getLevelOption, LEVEL_OPTIONS, LEVEL } from '_utils';
import Events from '_utils/events';
import PuzzleService from '_services/rest/modules/puzzle';
import FormatErrors from '_common/mixins/FormatErrors';
import workerManager from '_common/mixins/workerManager';
import BoardGrid from '_common/components/board/BoardGrid.vue';
import CustomSelect from '_shared/CustomSelect.vue';
import createWallsWorker from '_utils/walls/createWorker';
import {
  _LOAD_PUZZLE_WALLS_MODEL_,
  CELL,
  CELL_SIZE,
  MODEL,
  MIN_DIMENSIONS,
  DENSITY_BASE,
  DENSITY_REFERENCE_SIZE,
  DENSITY_REFERENCE_OFFSET,
  DENSITY_ADJUSTMENT_STEP,
} from './PuzzleWallsModel';

const MAX_WORKERS = 4;
const ATTEMPTS_PER_WORKER = 20000;

export default {
  name: 'WallsForm',
  components: { BoardGrid, CustomSelect },
  mixins: [mixin, FormatErrors, workerManager],
  data() {
    return {
      model: cloneDeep(MODEL),
      cellSize: CELL_SIZE,
      errors: { name: { required: false } },
      bestResult: null,
      workersDone: 0,
      minDimensions: MIN_DIMENSIONS,
      levelOptions: LEVEL_OPTIONS,
      levelOption: LEVEL_OPTIONS[1],
    };
  },
  methods: {
    ...mapActions([
      'setToggle',
      'activeAlert',
      'setPuzzleWalls',
      'setHasChanges',
    ]),
    resolveWorkerErrorMessage(data) {
      if (data.errorCode) {
        return this.$t(`walls.alert.workerErrors.${data.errorCode}`);
      }

      return data.message || this.$t('walls.alert.generateFailed');
    },
    updateTable() {
      const { rows, columns } = this.model;
      const newTable = [];
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < columns; x += 1) {
          const existing = this.model.table.find(
            c => Number(c.positionx) === x && Number(c.positiony) === y,
          );
          newTable.push(
            existing || {
              ...CELL,
              id: y * columns + x,
              positionx: x,
              positiony: y,
            },
          );
        }
      }
      this.model.table = newTable;
      this.updateDensity();
      this.setHasChanges(true);
    },
    generate() {
      this.cancelPreviousGeneration();
      this.startGenerationLoader();
      this.workers = [];
      this.bestResult = null;
      this.workersDone = 0;

      const workerCount = this.resolveWorkerCount();
      const state = { finished: false, errorCount: 0 };

      for (let i = 0; i < workerCount; i += 1) {
        this.spawnWorker(i, workerCount, state);
      }
    },
    resolveWorkerCount() {
      return navigator.hardwareConcurrency
        ? Math.min(navigator.hardwareConcurrency, MAX_WORKERS)
        : 2;
    },
    spawnWorker(workerId, totalWorkers, state) {
      const worker = createWallsWorker();

      worker.onmessage = ({ data }) => {
        if (state.finished) return;
        this.handleWorkerMessage(data, worker, totalWorkers, state);
      };

      worker.onerror = err => {
        console.error(`WallsWorker ${workerId + 1} error:`, err);
        state.errorCount += 1;
        worker.terminate();

        if (state.errorCount === totalWorkers && !state.finished) {
          this.finishGeneration(
            state,
            false,
            this.$t('walls.alert.workerCritical'),
          );
        }
      };

      this.workers.push(worker);

      worker.postMessage({
        workerId,
        rows: this.model.rows,
        columns: this.model.columns,
        options: {
          density: this.model.settings.density / 100,
          totalAttempts: ATTEMPTS_PER_WORKER,
        },
      });
    },
    handleWorkerMessage(data, worker, totalWorkers, state) {
      const { status } = data;

      if (status === 'progress') {
        this.onWorkerProgress(data, state);
        return;
      }

      if (status === 'done') {
        this.onWorkerDone(data, totalWorkers, state);
        return;
      }

      if (status === 'error') {
        state.errorCount += 1;
        worker.terminate();

        if (state.errorCount === totalWorkers && !state.finished) {
          this.finishGeneration(
            state,
            false,
            this.resolveWorkerErrorMessage(data),
          );
        }
      }
    },
    onWorkerProgress(data, state) {
      const { table, junkCount, workerId } = data;

      const isBetter =
        !this.bestResult || junkCount < this.bestResult.junkCount;

      if (isBetter) {
        this.bestResult = { table, junkCount, workerId };
        this.model.table = table;
      }

      if (junkCount === 0) {
        this.finishGeneration(
          state,
          true,
          this.$t('walls.alert.generated'),
        );
      }
    },
    onWorkerDone(data, totalWorkers, state) {
      const { table, junkCount, workerId } = data;

      const isBetter =
        !this.bestResult || junkCount < this.bestResult.junkCount;

      if (isBetter) {
        this.bestResult = { table, junkCount, workerId };
        this.model.table = table;
      }

      this.workersDone += 1;

      if (this.workersDone >= totalWorkers) {
        const junk = this.bestResult ? this.bestResult.junkCount : -1;
        const msg =
          junk === 0
            ? this.$t('walls.alert.generated')
            : this.$t('walls.alert.bestPuzzle', { junk });

        this.finishGeneration(state, true, msg);
      }
    },
    finishGeneration(state, success, message) {
      state.finished = true;
      this.cleanupWorkers();

      this.activeAlert({
        message,
        theme: success ? 'success' : 'danger',
      });
    },
    save() {
      if (this.checkForm()) {
        const puzzle = {
          ...this.model,
          slug: `${this.model.name.replace(/\s+/g, '-')}-walls`,
        };
        PuzzleService.save(puzzle)
          .then(response => {
            this.activeAlert({
              message: this.$t('walls.alert.gameSaved'),
              theme: 'success',
            });
            this.setPuzzleWalls(response);
            if (this.$route.name === 'admin-puzzle-walls-new') {
              this.$router.push({
                name: 'admin-puzzle-walls-edit',
                params: { puzzleId: response.id },
              });
            }
            this.setHasChanges(false);
          })
          .catch(error => {
            const errors = this.formatErrors(error.data);
            this.activeAlert({ message: errors, theme: 'danger' });
          });
      }
    },
    calculateDensityOffset(rows, columns) {
      const area = rows * columns;
      let adjustment = Math.sqrt(area);

      if (adjustment >= DENSITY_REFERENCE_SIZE) {
        adjustment =
          DENSITY_REFERENCE_OFFSET -
          (adjustment - DENSITY_REFERENCE_SIZE) * DENSITY_ADJUSTMENT_STEP;
      }

      const min = DENSITY_BASE - adjustment;
      const med = min + DENSITY_REFERENCE_OFFSET;
      const max = med + DENSITY_REFERENCE_OFFSET;

      return { min, med, max };
    },
    calculateDensity(level) {
      const { rows, columns } = this.model;
      const { min, med, max } = this.calculateDensityOffset(rows, columns);

      const densities = {
        [LEVEL.EASY]: min,
        [LEVEL.MEDIUM]: med,
        [LEVEL.HARD]: max,
      };

      return Math.round(densities[level]);
    },
    updateDensity() {
      this.model.settings.density = this.calculateDensity(this.model.level);
    },
    setLevel(option) {
      const level = option.slug;
      this.model.level = level;
      this.updateDensity();
      this.setHasChanges(true);
    },
  },
  mounted() {
    Events.$on(_LOAD_PUZZLE_WALLS_MODEL_, model => {
      this.model = { ...model };
      this.levelOption = getLevelOption(model.level);
    });
  },
  beforeDestroy() {
    Events.$off(_LOAD_PUZZLE_WALLS_MODEL_);
  },
};
</script>
